import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {CartesianTools} from 'raain-model';
import {LatLngBounds} from 'leaflet';
import {
    CartesianMapValue,
    ChartScaleColors,
    ElementsFactory,
    FrameContainer,
    MapElement,
    MapLatLng,
    RaainDivIcon,
    ScaleElement,
    ScaleElementInput,
    TimeframeContainers,
} from 'raain-ui';
import {Storage} from '../storage.service';

@Component({
    selector: 'raain-map',
    templateUrl: './raain-map.component.html',
    styleUrls: ['./raain-map.component.scss'],
})
export class RaainMapComponent implements AfterViewChecked, OnDestroy, OnChanges {
    @Input()
    public coordinates?: MapLatLng;
    @Input()
    public markers?: {
        gauges?: MapLatLng[];
        selectedGauges?: MapLatLng[];
        pixels?: MapLatLng[];
        borders?: MapLatLng[];
        speeds?: MapLatLng[];
        gaugesInCompare?: MapLatLng[];
        pixelsSolution?: MapLatLng[];
    };
    @Input()
    public timeframeContainers?: TimeframeContainers;
    @Input()
    public autoplay = false;
    @Input()
    public showMarkers = true;
    @Input()
    public showSpeedMarkers = true;
    @Input()
    public showVisiblePixelMarkers = false;
    @Input()
    public showCumulative = false;
    @Input()
    public cumulativeDurationInMinutes = 60;
    @Input()
    public scaleLabel = 'mm';
    @Input()
    public currentHeight: number;

    public showMapSettings = false;
    public currentWidth: number;

    @Input()
    public timeframeDates: Date[] = [];
    @Input()
    public defaultDate?: Date;

    @Input()
    public sumValues?: CartesianMapValue[];
    @Input()
    public sumFn?: (cartesianMapValues: CartesianMapValue[]) => number;
    public sumResult: string;

    @Output()
    public selectedMarker = new EventEmitter<MapLatLng>();

    @Output()
    public changedDate = new EventEmitter<Date>();

    @Output()
    public changedSum = new EventEmitter<string>();

    public alpha: number;
    public mcText;
    public nextDate: Date;
    public possibleNames: string[];
    public possibleDates: Date[];
    public dateToShow: Date;
    public dateToShowAsString: string;
    public timeframeNameToShow: string;
    public pixelsCount: number;
    public cumulativeDateFrom: Date;
    protected currentFrameContainer: FrameContainer;
    protected cartesianTools: CartesianTools;
    @ViewChild('primaryMap')
    protected mapDivRef: ElementRef;
    @ViewChild('canvasScale')
    protected scaleCanvasRef: ElementRef;
    private visiblePixelMarkers: MapLatLng[] = [];
    private mapElement: MapElement;
    private scaleElement: ScaleElement;

    private onSizeChanged;
    private created;
    private readonly raainDivIcons: {
        gauges: RaainDivIcon;
        selectedGauges: RaainDivIcon;
        pixels: RaainDivIcon;
        borders: RaainDivIcon;
        speeds: RaainDivIcon;
        gaugesInCompare: RaainDivIcon;
        pixelsSolution: RaainDivIcon;
    };

    constructor(
        private readonly ngZone: NgZone,
        private readonly storage: Storage,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.delete();

        this.cartesianTools = new CartesianTools();
        this.pixelsCount = 1;

        const iconSize: [number, number] = [40, 40]; // size of the icon
        const iconAnchor: [number, number] = [20, 20]; // point of the icon which will correspond to marker's location
        const pixelSize: [number, number] = [41, 41];
        const pixelAnchor: [number, number] = [20, 20];
        const popupAnchor: [number, number] = [0, -20]; // point from which the popup should open relative to the iconAnchor

        this.raainDivIcons = {
            gauges: new RaainDivIcon({
                html: '<img src="./assets/maps/marker-pluvio.png" style="width: 100%; height: 100%;" />',
                iconSize,
                iconAnchor,
            }),
            gaugesInCompare: new RaainDivIcon({
                html: '<img src="./assets/maps/marker-pluvio-diag.png" style="width: 100%; height: 100%;" />',
                iconSize,
                iconAnchor,
                popupAnchor,
            }),
            selectedGauges: new RaainDivIcon({
                html: '<img src="./assets/maps/marker-pluvio-diag-select.png" style="width: 100%; height: 100%;" />',
                iconSize,
                iconAnchor,
                popupAnchor,
            }),
            pixels: new RaainDivIcon({
                html: '<img src="./assets/maps/marker-rain.png" style="width: 100%; height: 100%;" />',
                iconSize,
                iconAnchor,
                popupAnchor,
            }),
            pixelsSolution: new RaainDivIcon({
                html: '<img src="./assets/maps/marker-rain.png" style="width: 100%; height: 100%;" />',
                iconSize: pixelSize,
                iconAnchor: pixelAnchor,
                popupAnchor,
            }),
            borders: new RaainDivIcon({
                html: '<img src="./assets/maps/marker-shadow.png" style="width: 100%; height: 100%;" />',
                iconSize,
                iconAnchor,
                popupAnchor,
            }),
            speeds: new RaainDivIcon({
                html: '<img src="./assets/maps/marker-speed.svg" style="width: 100%; height: 100%;" />',
                iconSize,
                iconAnchor,
                popupAnchor,
            }),
        };
    }

    public ngAfterViewChecked(): void {
        this.create();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.change(changes);
    }

    public ngOnDestroy(): void {
        this.delete();
    }

    public showTimeframe(force = false) {
        if (
            !this.dateToShowAsString ||
            (!force && this.areDatesEqual(this.dateToShowAsString, this.dateToShow))
        ) {
            return;
        }

        this.dateToShow = new Date(this.dateToShowAsString);
        this.changedDate.emit(this.dateToShow);
    }

    public next() {
        if (!this.possibleDates || this.possibleDates.length === 0) {
            return;
        }

        const index = this.getDateToShowIndex();
        this.dateToShow =
            index + 1 < this.possibleDates.length
                ? this.possibleDates[index + 1]
                : this.possibleDates[0];
        this.dateToShowAsString = this.dateToShow.toISOString();

        this.showTimeframe(true);
        if (this.autoplay) {
            window.setTimeout(() => this.next(), 2000);
        }
    }

    public previous() {
        this.stop();
        if (!this.possibleDates || this.possibleDates.length === 0) {
            return;
        }

        const index = this.getDateToShowIndex();
        this.dateToShow = index > 0 ? this.possibleDates[index - 1] : this.possibleDates[0];
        this.dateToShowAsString = this.dateToShow.toISOString();

        this.showTimeframe(true);
    }

    public play() {
        this.autoplay = true;
        this.next();
    }

    public stop() {
        this.autoplay = false;
    }

    public changeShowMarkers() {
        if (!this.mapElement?.markersLayer || !this.mapElement?.mapLeaflet) {
            return;
        }

        if (this.showMarkers) {
            this.mapElement.markersLayer.addToMap(this.mapElement.mapLeaflet);
        } else {
            this.mapElement.markersLayer.removeFromMap(this.mapElement.mapLeaflet);
        }
    }

    public changeShowSpeedMarkers() {
        if (!this.mapElement?.markersLayer || !this.mapElement?.mapLeaflet) {
            return;
        }

        // Rebuild markers with current visibility settings
        const {allMarkers} = this.buildMarkersConfig();
        const markersProduced = this.mapElement.updateMarkers(allMarkers);
        if (markersProduced?.length) {
            this.bindMarkersClickEvents(markersProduced);
            if (this.showMarkers) {
                this.mapElement.markersLayer.removeFromMap(this.mapElement.mapLeaflet);
                this.mapElement.markersLayer.addToMap(this.mapElement.mapLeaflet);
            }
        }
    }

    public changeAlpha() {
        this.storage.set('raain-map-alpha', this.alpha);
        if (!this.mapElement?.mapLeaflet) {
            return;
        }

        this.mapElement.alpha = 0.3 + this.alpha * 0.7;
        this.mapElement.mapLeaflet.eachLayer((layer: any) => {
            if (layer._container?.style) {
                layer._container.style.filter = `brightness(${(10 - this.alpha * 10) * 10}%)`; // invert(1) ?
            }
        });
    }

    public toggleMapSettings() {
        this.showMapSettings = !this.showMapSettings;
    }

    private create() {
        const created = this.created;
        if (created) {
            return;
        }
        this.created = true;

        const {allMarkers} = this.buildMarkersConfig();

        const factory = new ElementsFactory(this.coordinates, true);
        this.createScale(factory, this.scaleLabel);

        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                const createdOutside = this.created;
                if (!createdOutside) {
                    return;
                }

                this.mapElement = factory.createMap(this.mapDivRef.nativeElement, {
                    timeframeContainers: this.timeframeContainers,
                    markers: allMarkers,
                });

                this.changeAlpha();

                this.mapElement.mapLeaflet?.on('mousemove', (evt) => this.onMapMouseMove(evt));
                this.mapElement.mapLeaflet?.on('zoom', (evt) => this.onZoom(evt));
                this.mapElement.mapLeaflet?.on('moveend', (evt) => this.onMoveEnd(evt));
                const bounds: LatLngBounds = this.mapElement.mapLeaflet.getBounds();
                this.pixelsCount = this.cartesianTools.howManyPixelsInEarthMap(
                    bounds._southWest,
                    bounds._northEast
                );

                this.bindMarkersClickEvents(this.mapElement.markersProduced);
                this.onSizeChanged = () => this.updateLayerDimensions();
                this.updateMapSize();
                this.changeNavigation(this.defaultDate);
            }, 100);
        });
    }

    private delete() {
        this.mapElement?.mapLeaflet?.remove();
        this.mapElement = undefined;
        this.mcText = '';
        this.nextDate = undefined;
        this.possibleNames = [];
        this.possibleDates = [];
        this.timeframeNameToShow = undefined;
        this.dateToShow = undefined;
        this.dateToShowAsString = undefined;
        this.cumulativeDateFrom = undefined;
        this.onSizeChanged = () => {};
        this.currentFrameContainer = null;

        this.created = false;
        this.alpha = this.storage.get('raain-map-alpha', 0.4);
    }

    private change(changes: SimpleChanges) {
        let possibleNewDefaultDate: Date;

        if (changes.defaultDate) {
            if (
                this.areDatesEqual(
                    changes.defaultDate.currentValue,
                    changes.defaultDate.previousValue
                )
            ) {
                return;
            }
            possibleNewDefaultDate = this.defaultDate;
        }

        if (changes.markers && this.mapElement?.markersLayer) {
            const {allMarkers} = this.buildMarkersConfig();
            const markersProduced = this.mapElement.updateMarkers(allMarkers);
            if (markersProduced?.length) {
                this.bindMarkersClickEvents(markersProduced);
                if (this.showMarkers) {
                    this.mapElement.markersLayer.removeFromMap(this.mapElement.mapLeaflet);
                    this.mapElement.markersLayer.addToMap(this.mapElement.mapLeaflet);
                }
            }
        }

        if (
            changes.markers &&
            this.markers?.selectedGauges &&
            this.markers?.selectedGauges.length === 1
        ) {
            const selectedGauge = this.markers.selectedGauges[0];
            this.mapElement?.mapLeaflet?.setView(selectedGauge, 10);
        }

        if (this.mapElement && changes.timeframeContainers) {
            this.updateTimeframe();
        }

        if (this.mapElement?.compositeLayer && changes.sumValues) {
            this.updateSum();
        }

        if (this.mapElement?.compositeLayer && changes.showVisiblePixelMarkers) {
            const drawer = this.mapElement.compositeLayer.getFirstDrawer();
            this.updateVisiblePixelMarkers(drawer);
        }

        this.changeNavigation(possibleNewDefaultDate);
    }

    private updateSum() {
        if (!this.sumValues || !this.sumFn) {
            return;
        }

        const drawer = this.mapElement.compositeLayer.getFirstDrawer();
        const sumOfPixels: number = drawer?.getExecOfWindowPoints(this.sumValues, this.sumFn) ?? -1;

        if (sumOfPixels >= 0 && this.pixelsCount > 0) {
            const average = Math.round((sumOfPixels / this.pixelsCount) * 100) / 100;
            this.sumResult = `${Math.round(sumOfPixels)}mm (avg of ${average}mm for ${Math.round(this.pixelsCount)} pixels)`;
        } else {
            this.sumResult = 'NA';
        }
        this.changedSum.emit(this.sumResult);

        this.updateVisiblePixelMarkers(drawer);
    }

    private updateVisiblePixelMarkers(drawer: any) {
        if (!this.showVisiblePixelMarkers || !drawer || !this.sumValues) {
            if (this.visiblePixelMarkers.length > 0) {
                this.visiblePixelMarkers = [];
                this.refreshMarkersLayer();
            }
            return;
        }

        // Extract visible pixel positions
        const visiblePixels: MapLatLng[] =
            drawer.getExecOfWindowPoints(this.sumValues, (values: CartesianMapValue[]) =>
                values.map((v) => ({
                    lat: (v.lat + v.lat2) / 2,
                    lng: (v.lng + v.lng2) / 2,
                    value: v.value,
                }))
            ) ?? [];

        this.visiblePixelMarkers = visiblePixels;
        this.refreshMarkersLayer();
    }

    private refreshMarkersLayer() {
        if (!this.mapElement?.markersLayer || !this.mapElement?.mapLeaflet) {
            return;
        }

        const {allMarkers} = this.buildMarkersConfig();
        const markersProduced = this.mapElement.updateMarkers(allMarkers);
        if (markersProduced?.length) {
            this.bindMarkersClickEvents(markersProduced);
            if (this.showMarkers) {
                this.mapElement.markersLayer.removeFromMap(this.mapElement.mapLeaflet);
                this.mapElement.markersLayer.addToMap(this.mapElement.mapLeaflet);
            }
        }
    }

    private changeNavigation(defaultDate?: Date) {
        // update possible names
        if (this.timeframeContainers?.containers?.length) {
            this.possibleNames = [];
            this.timeframeContainers.containers.forEach((c) => {
                if (this.possibleNames.indexOf(c.name) < 0) {
                    this.possibleNames.push(c.name);
                }
            });
            this.timeframeNameToShow = this.possibleNames.length > 0 ? this.possibleNames[0] : null;
        }

        // update possible dates
        this.possibleDates = this.timeframeDates;
        if (!this.possibleDates || this.possibleDates.length === 0) {
            return;
        }

        // update dateToShow
        if (
            defaultDate &&
            this.possibleDates.filter((d) => this.areDatesEqual(d, defaultDate)).length === 1
        ) {
            this.dateToShow = defaultDate;
        }
        if (
            !this.dateToShow &&
            this.possibleDates.length > 0 &&
            this.possibleDates.filter((d) => this.areDatesEqual(d, this.dateToShow)).length !== 1
        ) {
            this.dateToShow = this.possibleDates[0];
        }

        let firstInit = false;
        if (!this.dateToShowAsString) {
            firstInit = true;
        }
        this.dateToShowAsString = this.dateToShow.toISOString();
        this.cumulativeDateFrom = new Date(this.dateToShow);

        const durationInMs = this.cumulativeDurationInMinutes * 60 * 1000;
        this.cumulativeDateFrom.setTime(this.cumulativeDateFrom.getTime() - durationInMs);

        if (firstInit) {
            this.showTimeframe(true);
        }
    }

    private getDateToShowIndex(): number {
        let index = 0;
        for (const date of this.possibleDates) {
            if (this.areDateISOEqual(this.dateToShowAsString, date)) {
                break;
            }
            index++;
        }
        return index;
    }

    private onZoom(evt: any) {
        const map = evt.target;
        const bounds: LatLngBounds = map.getBounds();
        this.pixelsCount = this.cartesianTools.howManyPixelsInEarthMap(
            bounds._southWest,
            bounds._northEast
        );
        this.updateSum();
    }

    private onMoveEnd(evt: any) {
        const map = evt.target;
        const bounds: LatLngBounds = map.getBounds();
        this.pixelsCount = this.cartesianTools.howManyPixelsInEarthMap(
            bounds._southWest,
            bounds._northEast
        );
        this.updateSum();
    }

    private handleAvailablePixels(
        availablePixels: {width: number; height: number; totalPixels: number},
        bounds: any,
        zoom: number
    ) {
        // Update your component properties or perform calculations
        this.currentWidth = availablePixels.width;
        this.currentHeight = availablePixels.height;

        // You can also calculate pixel density or other metrics here
        // const pixelDensity = this.calculatePixelDensity(bounds, availablePixels, zoom);
        // console.log('Pixel density:', pixelDensity);
    }

    private calculatePixelDensity(
        bounds: any,
        availablePixels: {width: number; height: number},
        zoom: number
    ) {
        // Calculate how many meters per pixel (approximate)
        const earthCircumference = 40075016.686; // meters
        const metersPerPixel =
            (earthCircumference * Math.cos((bounds.getCenter().lat * Math.PI) / 180)) /
            Math.pow(2, zoom + 8);

        return {
            metersPerPixel,
            pixelsPerMeter: 1 / metersPerPixel,
            totalVisibleArea: availablePixels.width * availablePixels.height,
        };
    }

    private onMapMouseMove(evt: {latlng: {lat: number; lng: number}}): void {
        const lat: string = evt.latlng.lat.toFixed(3);
        const lng: string = evt.latlng.lng.toFixed(3);

        let value = NaN;
        let valueLat = NaN,
            valueLng = NaN,
            valueLat2 = NaN,
            valueLng2 = NaN;
        if (this.currentFrameContainer) {
            const valueSelected = (this.currentFrameContainer.values as CartesianMapValue[]).filter(
                (v: CartesianMapValue) => {
                    return (
                        v.lat <= evt.latlng.lat &&
                        evt.latlng.lat < v.lat2 &&
                        v.lng <= evt.latlng.lng &&
                        evt.latlng.lng < v.lng2
                    );
                }
            );

            if (valueSelected.length === 1) {
                value = this.roundTo3Decimals(valueSelected[0].value);
                valueLat = this.roundTo3Decimals(valueSelected[0].lat);
                valueLng = this.roundTo3Decimals(valueSelected[0].lng);
                valueLat2 = this.roundTo3Decimals(valueSelected[0].lat2);
                valueLng2 = this.roundTo3Decimals(valueSelected[0].lng2);
            } else {
                // console.warn('valueSelected issue ?', valueSelected);
            }
        }

        this.ngZone.run(() => {
            // this.mcText = `Latitude: ${lat} &nbsp; &nbsp; Longitude: ${lng} &nbsp; &nbsp;
            //    Value: [${valueLat} ${valueLng} | ${valueLat2} ${valueLng2}] ${value}`;
            this.mcText = `Latitude: ${lat} &nbsp; &nbsp; Longitude: ${lng} &nbsp; &nbsp; Value: ${value}`;
            this.cdr.markForCheck();
        });
    }

    private onMapClick(evt: any): void {
        if (!this.markers?.gauges?.length) {
            return;
        }

        let selectedGauge: MapLatLng;
        for (const gauge of this.markers.gauges) {
            if (gauge.id === evt?.target?.options?.alt) {
                selectedGauge = gauge;
                break;
            }
        }

        if (selectedGauge) {
            this.markers.selectedGauges = [selectedGauge];
            this.selectedMarker.emit(
                new MapLatLng(
                    selectedGauge.lat,
                    selectedGauge.lng,
                    undefined,
                    selectedGauge.id,
                    selectedGauge.name,
                    selectedGauge.value
                )
            );
        } else {
            this.selectedMarker.emit(
                new MapLatLng(evt.latlng?.lat, evt.latlng?.lng, undefined, evt.id, evt.name)
            );
        }

        this.change({markers: ['changed']} as any);
    }

    @HostListener('window:resize', ['$event'])
    private onResize(event: any): void {
        this.updateMapSize();
    }

    private updateMapSize(): void {
        const mapDivWidth = this.mapDivRef.nativeElement.parentNode?.parentNode['clientWidth'];
        const padding = 21;
        let currentWidth =
            mapDivWidth ||
            window.innerWidth ||
            document.documentElement.clientWidth ||
            document.body.clientWidth;
        const currentHeight =
            this.currentHeight ||
            window.innerHeight ||
            document.documentElement.clientHeight ||
            document.body.clientHeight;
        currentWidth -= padding;
        this.currentWidth = currentWidth;
        this.currentHeight = currentHeight;

        this.ngZone.run(() => {
            this.onSizeChanged();
        });
    }

    private updateTimeframe() {
        if (
            this.timeframeContainers?.containers?.length &&
            this.timeframeContainers?.containers[0]?.timeframe?.length
        ) {
            this.mapElement.updateMapTimeframe(this.timeframeContainers);
            this.currentFrameContainer = this.timeframeContainers?.containers[0]?.timeframe[0];
        }

        this.changeAlpha();
    }

    private createScale(factory: ElementsFactory, label: string) {
        this.deleteScale();
        if (!this.scaleCanvasRef?.nativeElement) {
            return;
        }

        const entries = Object.entries(ChartScaleColors);
        entries.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));
        const sortedMap = new Map(entries);
        const sortedArray = [...sortedMap.entries()];
        const scaleColors = sortedArray.map((entry) => {
            return {color: entry[1]};
        });
        const scaleLabels = sortedArray.map((entry) => entry[0]);
        this.scaleElement = factory.createScale(
            this.scaleCanvasRef.nativeElement,
            new ScaleElementInput(scaleColors, scaleLabels, label)
        );
    }

    private deleteScale() {
        if (this.scaleElement) {
            this.scaleElement?.chart?.destroy();
        }
    }

    /**
     * Builds the markers configuration array for the map
     * @returns Array of marker configurations with icons and positions
     */
    private buildMarkersConfig() {
        const allMarkers: {iconsLatLng: MapLatLng[]; raainDivIcon: RaainDivIcon}[] = [
            {iconsLatLng: this.markers?.gauges ?? [], raainDivIcon: this.raainDivIcons.gauges},
            {
                iconsLatLng: this.markers?.selectedGauges ?? [],
                raainDivIcon: this.raainDivIcons.selectedGauges,
            },
            {
                iconsLatLng: this.markers?.gaugesInCompare ?? [],
                raainDivIcon: this.raainDivIcons.gaugesInCompare,
            },
            {iconsLatLng: this.markers?.pixels ?? [], raainDivIcon: this.raainDivIcons.pixels},
            {
                iconsLatLng: this.markers?.pixelsSolution ?? [],
                raainDivIcon: this.raainDivIcons.pixelsSolution,
            },
            // {iconsLatLng: this.markers?.borders, raainDivIcon: this.raainDivIcons.borders},
        ];

        if (this.showVisiblePixelMarkers && this.visiblePixelMarkers.length > 0) {
            allMarkers.push({
                iconsLatLng: this.visiblePixelMarkers,
                raainDivIcon: this.raainDivIcons.pixelsSolution,
            });
        }

        if (this.showSpeedMarkers) {
            for (const marker of this.markers?.speeds ?? []) {
                // Calculate dynamic size based on speed (marker.value is in m/s)
                // Base size: 20px, scale up with speed (typical range: 1-20 m/s)
                const speedInMetersPerSec = marker.value ?? 5;
                const minSize = 20;
                const maxSize = 60;
                const minSpeed = 1;
                const maxSpeed = 20;

                // Linear interpolation: size increases with speed
                const normalizedSpeed = Math.max(minSpeed, Math.min(maxSpeed, speedInMetersPerSec));
                const size =
                    minSize +
                    ((normalizedSpeed - minSpeed) / (maxSpeed - minSpeed)) * (maxSize - minSize);
                const iconSize = Math.round(size);

                allMarkers.push({
                    iconsLatLng: [marker],
                    raainDivIcon: new RaainDivIcon({
                        html: this.raainDivIcons.speeds.options.html,
                        width: iconSize,
                        height: iconSize,
                        iconAnchor: [iconSize / 2, iconSize / 2], // Center the icon
                        popupAnchor: this.raainDivIcons.speeds.options.popupAnchor,
                        rotationAngle: marker.alt, // Use alt property for azimuth/direction
                        className: this.raainDivIcons.speeds.options.className,
                    }),
                });
            }
        }
        return {allMarkers};
    }

    /**
     * Compare two dates for equality using their timestamps
     * @param date1 First date (Date or string)
     * @param date2 Second date (Date or string)
     * @returns true if dates are equal
     */
    private areDatesEqual(date1: Date | string, date2: Date | string): boolean {
        if (!date1 || !date2) {
            return false;
        }
        const timestamp1 = typeof date1 === 'string' ? new Date(date1).getTime() : date1.getTime();
        const timestamp2 = typeof date2 === 'string' ? new Date(date2).getTime() : date2.getTime();
        return timestamp1 === timestamp2;
    }

    /**
     * Compare an ISO date string with a Date object (comparing first 19 characters)
     * @param dateISO ISO date string
     * @param date Date object
     * @returns true if dates match (up to seconds precision)
     */
    private areDateISOEqual(dateISO: string, date: Date): boolean {
        if (!dateISO || !date) {
            return false;
        }
        return dateISO.substring(0, 19) === date.toISOString().substring(0, 19);
    }

    /**
     * Bind click event handlers to markers
     * @param markers Array of marker objects to bind events to
     */
    private bindMarkersClickEvents(markers: any[]): void {
        if (!markers) {
            return;
        }
        markers.forEach((m) => m.on('click', (evt) => this.onMapClick(evt)));
    }

    /**
     * Round a number to 3 decimal places
     * @param value Number to round
     * @returns Rounded number
     */
    private roundTo3Decimals(value: number): number {
        return Math.round(value * 1000) / 1000;
    }

    /**
     * Update layer dimensions and invalidate map size
     */
    private updateLayerDimensions(): void {
        if (!this.mapElement) {
            return;
        }
        this.mapElement.markersLayer?.setCurrentWidth(this.currentWidth);
        this.mapElement.markersLayer?.setCurrentHeight(this.currentHeight);
        this.mapElement.compositeLayer?.setCurrentWidth(this.currentWidth);
        this.mapElement.compositeLayer?.setCurrentHeight(this.currentHeight);
        this.mapElement.mapLeaflet?.invalidateSize({animate: true});
    }
}
